import { useState } from "preact/hooks";
import type { IssuedKey } from "./api";
import { createWorkspace, issueKey, revokeKey } from "./api";
import { Button, Checkbox } from "./ui";

/**
 * 발급 UI — 워크스페이스 만들기, 소비자 키 발급, 폐기.
 *
 * 세 가지가 이 화면의 설계 전부다:
 *
 * 1. **secret 은 한 번만 보인다.** 서버가 다시 내주지 않으므로 발급 결과는 닫기 전까지
 *    화면에 남기고, 복사 버튼과 경고를 같이 둔다. localStorage 에 넣지 않는다 — 브라우저에
 *    비밀을 남기지 않는 게 이 콘솔의 전제다(세션 쿠키조차 HttpOnly 라 JS 가 못 읽는다).
 * 2. **admin 키 발급은 여기 없다.** 세션은 자기보다 센 자격증명을 만들 수 없다
 *    (컨트롤러 `sessionMayWrite`). 필요하면 admin 키로 CLI 에서 한다.
 * 3. 폐기는 **부트스트랩(env) 키에는 안 붙는다** — 그건 배포 설정에 박힌 값이라 여기서
 *    지울 수 있는 것이 아니다.
 */

function Field({
  label,
  hint,
  value,
  onInput,
  placeholder,
  type = "text",
}: {
  label: string;
  hint?: string;
  value: string;
  onInput: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label class="field">
      <span class="flabel">{label}</span>
      <input
        class="finput"
        type={type}
        value={value}
        placeholder={placeholder}
        onInput={(e) => onInput((e.target as HTMLInputElement).value)}
      />
      {hint ? <span class="fhint">{hint}</span> : null}
    </label>
  );
}

/** 발급 결과. 이 컴포넌트가 살아 있는 동안만 secret 이 존재한다. */
function Issued({ issued, onDone }: { issued: IssuedKey; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const env = [
    `TSBX_URL=${location.origin.replace("console.", "")}`,
    `TSBX_KEY_ID=${issued.keyId}`,
    `TSBX_SECRET=${issued.secret}`,
  ].join("\n");
  return (
    <div class="issued">
      <h4>키가 발급됐다</h4>
      <p class="dnote">
        {issued.workspace ? `워크스페이스 ${issued.workspace} · ` : ""}
        {issued.expiresAt ? `만료 ${new Date(issued.expiresAt).toLocaleString("ko-KR")}` : "무기한"}
      </p>
      <pre class="secret">{env}</pre>
      <p class="dnote warn">
        secret 은 지금이 유일한 노출이다. 창을 닫으면 다시 볼 수 없고, 잃으면 폐기하고 다시
        발급해야 한다.
      </p>
      <div class="confirm">
        <Button
          onClick={() => {
            void navigator.clipboard?.writeText(env).then(() => setCopied(true));
          }}
        >
          {copied ? "복사됨" : "복사"}
        </Button>
        <Button onClick={onDone}>닫기</Button>
      </div>
    </div>
  );
}

export function NewWorkspace({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [maxSandboxes, setMaxSandboxes] = useState("");
  const [maxVolumeGi, setMaxVolumeGi] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await createWorkspace({
        name: name.trim(),
        ...(maxSandboxes ? { maxSandboxes: Number(maxSandboxes) } : {}),
        ...(maxVolumeGi ? { maxVolumeGi: Number(maxVolumeGi) } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="form">
      <Field
        label="이름"
        hint="리소스 라벨이 되므로 소문자·숫자·하이픈만. 나중에 바꿀 수 없다."
        value={name}
        onInput={setName}
        placeholder="acme"
      />
      <div class="frow">
        <Field
          label="샌드박스 한도"
          hint="비우면 무제한"
          value={maxSandboxes}
          onInput={setMaxSandboxes}
          placeholder="20"
          type="number"
        />
        <Field
          label="볼륨 한도 (GiB)"
          hint="비우면 무제한"
          value={maxVolumeGi}
          onInput={setMaxVolumeGi}
          placeholder="200"
          type="number"
        />
      </div>
      <Field label="메모" value={note} onInput={setNote} placeholder="누구에게 주는 것인지" />
      {error ? <p class="dnote bad">{error}</p> : null}
      <div class="confirm">
        <Button disabled={busy || name.trim().length === 0} onClick={() => void submit()}>
          {busy ? "만드는 중" : "만들기"}
        </Button>
        <Button disabled={busy} onClick={onDone}>
          취소
        </Button>
      </div>
    </div>
  );
}

export function NewKey({ workspace, onDone }: { workspace: string; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [ttlDays, setTtlDays] = useState("180");
  const [forever, setForever] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [issued, setIssued] = useState<IssuedKey | null>(null);

  if (issued) return <Issued issued={issued} onDone={onDone} />;

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      setIssued(
        await issueKey(workspace, {
          ...(note.trim() ? { note: note.trim() } : {}),
          ...(forever || !ttlDays ? {} : { ttlSec: Math.round(Number(ttlDays) * 86_400) }),
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="form">
      <p class="dnote">
        <b>{workspace}</b> 의 소비자 키를 발급한다. 이 키로는 그 워크스페이스의 샌드박스만
        다룰 수 있다.
      </p>
      <Field
        label="메모"
        hint="누구에게 준 키인지 적어 두세요. 나중에 폐기할 때 이것만 보고 고르게 됩니다."
        value={note}
        onInput={setNote}
        placeholder="acme prod"
      />
      {!forever ? (
        <Field label="유효기간 (일)" value={ttlDays} onInput={setTtlDays} type="number" />
      ) : null}
      <Checkbox checked={forever} onToggle={setForever}>
        {/* 무기한을 기본으로 두지 않는다 — 폐기를 사람이 기억해야 하는 유일한 종류다. */}
        무기한 (폐기를 사람이 기억해야 한다)
      </Checkbox>
      {error ? <p class="dnote bad">{error}</p> : null}
      <div class="confirm">
        <Button disabled={busy} onClick={() => void submit()}>
          {busy ? "발급 중" : "발급"}
        </Button>
        <Button disabled={busy} onClick={onDone}>
          취소
        </Button>
      </div>
    </div>
  );
}

export function RevokeKey({ keyId, onDone }: { keyId: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  return (
    <div class="form">
      <p class="dnote">
        <b>{keyId}</b> 를 폐기한다. 이 키를 쓰는 소비자는 <b>다음 요청부터 401</b> 이 되고,
        되돌릴 수 없다 (새로 발급해야 한다).
      </p>
      {error ? <p class="dnote bad">{error}</p> : null}
      <div class="confirm">
        <Button
          tone="danger"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            revokeKey(keyId)
              .then(onDone)
              .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : String(err));
                setBusy(false);
              });
          }}
        >
          {busy ? "폐기 중" : "폐기"}
        </Button>
        <Button disabled={busy} onClick={onDone}>
          취소
        </Button>
      </div>
    </div>
  );
}
